import { ForeignNationalDocumentsAPI } from "../../api/foreignNationalDocumentsApi";
import type { ForeignNationalDocument } from "../../api/types/foreignNationalDocument";
import type {
  EditableFile,
  EditableFileStore
} from "../caseFiles/editableFile";

const stem = (filename: string) =>
  filename.replace(/\.[^./]+$/, "") || filename;

export const clientDocumentFileOf = (
  doc: ForeignNationalDocument
): EditableFile => ({
  key: `client-document:${doc.foreign_national_document_id}`,
  id: doc.foreign_national_document_id,
  title: stem(doc.filename),
  filename: doc.filename,
  mimeType: doc.content_type || null,
  checksum: doc.checksum ?? null,
  revision: doc.storage_path || doc.checksum || null,
  versions: (doc.versions ?? []).map((v) => ({
    created_at: v.created_at ?? null,
    note: v.note ?? null
  })),
  // The bytes are read by document id, so a stand-in record without a
  // storage path (a checklist row ahead of the list) still opens.
  hasBytes: true
});

/**
 * Turn the 1-based pages in `rotations` clockwise by their degrees, on top of
 * the rotation they already have. Case files have this done by the server.
 */
export const withRotations = async (
  pdf: Blob,
  rotations: Record<number, number> = {}
): Promise<Blob> => {
  const turns = Object.entries(rotations)
    .map(([page, deg]) => [Number(page), deg] as const)
    .filter(([, deg]) => deg % 360 !== 0);
  if (turns.length === 0) return new Blob([pdf], { type: "application/pdf" });
  const { PDFDocument, degrees } = await import("pdf-lib");
  // Owner-restricted PDFs (empty user password) still carry an Encrypt
  // dictionary. pdf-lib throws on those unless encryption is ignored, and
  // rewriting their object streams produces a file readers cannot open.
  const document = await PDFDocument.load(await pdf.arrayBuffer(), {
    ignoreEncryption: true
  });
  const count = document.getPageCount();
  turns
    .filter(([page]) => Number.isInteger(page) && page >= 1 && page <= count)
    .forEach(([page, deg]) => {
      const target = document.getPage(page - 1);
      const angle = (((target.getRotation().angle + deg) % 360) + 360) % 360;
      target.setRotation(degrees(angle));
    });
  const bytes = document.isEncrypted
    ? await document.save({ useObjectStreams: false })
    : await document.save();
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return new Blob([buffer], { type: "application/pdf" });
};

/**
 * A client document, open in the Case IDE's viewers and editors. Saves and
 * restores go through `.../documents/:id/versions` and `.../restore-version`;
 * `onChange` gets the updated document so the list can refresh.
 */
export const clientDocumentStore = (
  foreignNationalId: string,
  doc: ForeignNationalDocument,
  options: { onChange?: (doc: ForeignNationalDocument) => void } = {}
): EditableFileStore => {
  const id = doc.foreign_national_document_id;
  const changed = (updated: ForeignNationalDocument) => {
    options.onChange?.(updated);
    return clientDocumentFileOf(updated);
  };
  return {
    file: clientDocumentFileOf(doc),
    loadOriginal: () =>
      ForeignNationalDocumentsAPI.getFileBlob(foreignNationalId, id),
    loadPdf: () =>
      ForeignNationalDocumentsAPI.getFilePdfBlob(foreignNationalId, id),
    replaceBytes: async (blob, { note, baseChecksum }) =>
      changed(
        await ForeignNationalDocumentsAPI.saveVersion(
          foreignNationalId,
          id,
          blob,
          { note, baseChecksum }
        )
      ),
    replacePdf: async (pdf, { note, baseChecksum, rotations }) =>
      changed(
        await ForeignNationalDocumentsAPI.saveVersion(
          foreignNationalId,
          id,
          await withRotations(pdf, rotations),
          { note, baseChecksum }
        )
      ),
    restoreVersion: async (index) =>
      changed(
        await ForeignNationalDocumentsAPI.restoreVersion(
          foreignNationalId,
          id,
          index
        )
      )
  };
};
